/**
 * AnnotationDocksBuilder.ts — builds the family of {@link Dock}s
 * extruded from authored dock footprints,
 * plus the floating {@link Buoy}s that go with buoy-style docks.
 *
 * Each magenta blob becomes one Dock with piles + bollards +
 * collider AABB. Length / width / heading come from the OBB; the
 * rest (deck thickness, freeboard, materials) inherits from the
 * default dock spec via {@link dockSpecFromFootprint}.
 *
 * Buoy spawning intentionally lives here — buoys are only meaningful
 * relative to their parent dock's slip layout, and isolating that
 * coupling keeps the orchestrator (`startApp`) free of the per-slip
 * geometry maths.
 */
import {
  createDockMesh,
  createDockMooringMesh,
  mooringSlipLayout,
} from "../assets/ProceduralAssets";
import {
  sampleTerrainSurface,
  type TerrainAsset,
} from "../assets/TerrainAsset";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Buoy } from "./Buoy";
import type { BuoyKind } from "./BuoyLibrary";
import { dockSpecFromFootprint, type DockFootprint } from "./building-extrude";
import type { Aabb } from "./Collisions";
import { Dock } from "./Dock";

/**
 * Callback used by the dock builder to spawn one buoy at the given
 * world XZ. The caller (main.ts) is responsible for picking the
 * matching `BuoyLibrary` entry, instantiating the {@link Buoy}, and
 * registering its visual mesh + mooring-anchor record.
 *
 * Returns `null` when the spawn is rejected (e.g. duplicate id).
 */
export type BuoySpawnerFn = (req: {
  kind: BuoyKind;
  posX: number;
  posZ: number;
  id: string;
  anchored?: boolean;
}) => Buoy | null;

export interface AnnotationDocksBuildArgs {
  dockFootprints: readonly DockFootprint[];
  staticMeshPass: StaticMeshPass;
  /** Spawn one mooring buoy per slip on `buoy`-style docks. */
  spawnBuoy: BuoySpawnerFn;
  /** Receives one collision AABB per dock. */
  dockAabbs: Aabb[];
  waterLevel: number;
  /**
   * Optional terrain — when present, buoy candidates whose spawn point
   * falls on land (terrain elevation ≳ water level) are skipped instead
   * of being placed under the shore.
   */
  terrain?: TerrainAsset;
}

export interface AnnotationDocksResult {
  /** All docks built from annotation footprints (may be empty). */
  docks: Dock[];
}

/**
 * Build {@link Dock}s + their meshes / colliders / buoys from the
 * supplied footprints. Returns the docks in input order so callers
 * can index by `spec.id`.
 */
export function buildAnnotationDocks(
  args: AnnotationDocksBuildArgs,
): AnnotationDocksResult {
  const {
    dockFootprints,
    staticMeshPass,
    spawnBuoy,
    dockAabbs,
    waterLevel,
    terrain,
  } = args;

  const docks: Dock[] = [];
  let buoysSkipped = 0;
  let n = 0;
  for (const f of dockFootprints) {
    n++;
    const spec = dockSpecFromFootprint(f, `ann_dock_${n}`);
    const dock = new Dock(spec, {
      posX: f.x,
      posZ: f.z,
      headingY: f.yaw,
      waterLevel,
    });
    docks.push(dock);

    // Dock body mesh.
    const meshId = `ann_dock_mesh_${n}`;
    staticMeshPass.addGroup(meshId, createDockMesh(spec, meshId), 0, {
      castsHardShadow: true,
    });
    // Mooring infrastructure (buoys / posts / y-boom arms). Same
    // transform as the dock body.
    const moorMesh = createDockMooringMesh(spec, meshId + "_moor");
    if (moorMesh) {
      staticMeshPass.addGroup(meshId + "_moor", moorMesh, 0, {
        castsHardShadow: true,
      });
    }
    const cosY = Math.cos(dock.headingY);
    const sinY = Math.sin(dock.headingY);
    // prettier-ignore
    const m = new Float32Array([
      cosY,  0, sinY, 0,
      0,     1, 0,    0,
      -sinY, 0, cosY, 0,
      dock.posX, dock.deckTopY, dock.posZ, 1,
    ]) as Mat4;
    staticMeshPass.addInstance(meshId, m);
    if (moorMesh) {
      staticMeshPass.addInstance(meshId + "_moor", m);
    }

    // Spawn real Buoy sim instances for buoy-style mooring. One
    // buoy per slip on each long edge, ~5 m off the dock.
    if (spec.mooringStyle === "buoy") {
      buoysSkipped += spawnSlipBuoys({
        spec,
        dock,
        cosY,
        sinY,
        spawnBuoy,
        terrain,
      });
    }

    // Collision AABB enclosing the rotated dock rectangle (exact
    // when yaw is axis-aligned, conservative otherwise).
    dockAabbs.push(
      computeDockAabb({
        spec,
        dock,
        cosY,
        sinY,
        waterLevel,
      }),
    );
  }

  if (n > 0) {
    console.log(`[Docks] extruded ${n} docks from annotations`);
    for (const dock of docks) {
      console.log(
        `  • ${dock.spec.id}: ${dock.spec.length.toFixed(1)}×${dock.spec.width.toFixed(1)} m, mooring=${dock.spec.mooringStyle ?? "dock-only"}`,
      );
    }
    if (buoysSkipped > 0) {
      console.log(
        `[Docks] skipped ${buoysSkipped} buoy(s) whose spawn point lay on land`,
      );
    }
  }

  return { docks };
}

interface SlipBuoyArgs {
  spec: Dock["spec"];
  dock: Dock;
  cosY: number;
  sinY: number;
  spawnBuoy: BuoySpawnerFn;
  terrain?: TerrainAsset;
}

/**
 * A buoy is skipped when its spawn point doesn't classify as water
 * (i.e. the slip standoff projects the buoy onto the shore).
 * Annotations are checked first by `sampleTerrainSurface`, so a
 * dock-painted pixel directly under a buoy candidate would also flag
 * it as non-water — fine, those are degenerate footprints anyway.
 */
function isWaterAt(terrain: TerrainAsset, x: number, z: number): boolean {
  return sampleTerrainSurface(terrain, x, z) === "water";
}

/** @returns number of buoy candidates that were skipped (land-locked). */
function spawnSlipBuoys(args: SlipBuoyArgs): number {
  const { spec, dock, cosY, sinY, spawnBuoy, terrain } = args;
  const halfL = spec.length * 0.5;
  const halfW = spec.width * 0.5;
  const layout = mooringSlipLayout(spec);
  const standoff = layout.standoff;
  const slipCount = layout.slipCount;
  let skipped = 0;
  for (let side = 0; side < 2; side++) {
    const zSign = side === 0 ? -1 : 1;
    const zEdge = zSign * (halfW + standoff);
    for (let i = 0; i < slipCount; i++) {
      const t = slipCount === 1 ? 0.5 : (i + 0.5) / slipCount;
      const xCentre = -halfL + t * spec.length;
      // Local → world.
      const wx = dock.posX + cosY * xCentre + sinY * zEdge;
      const wz = dock.posZ - sinY * xCentre + cosY * zEdge;
      // Skip if the spawn point lies on dry ground — happens when a
      // dock footprint sits flush against the shore and the slip
      // standoff projects the buoy onto the bank.
      if (terrain && !isWaterAt(terrain, wx, wz)) {
        skipped++;
        continue;
      }
      spawnBuoy({
        kind: "mooring_standard",
        posX: wx,
        posZ: wz,
        id: `${spec.id}_buoy_${side}_${i}`,
        anchored: true,
      });
    }
  }
  return skipped;
}

interface DockAabbArgs {
  spec: Dock["spec"];
  dock: Dock;
  cosY: number;
  sinY: number;
  waterLevel: number;
}

function computeDockAabb(args: DockAabbArgs): Aabb {
  const { spec, dock, cosY, sinY, waterLevel } = args;
  const halfL = spec.length * 0.5;
  const halfW = spec.width * 0.5;
  const corners: Array<[number, number]> = [
    [+halfL, +halfW],
    [+halfL, -halfW],
    [-halfL, +halfW],
    [-halfL, -halfW],
  ];
  let minX = Infinity,
    maxX = -Infinity,
    minZ = Infinity,
    maxZ = -Infinity;
  for (const [lx, lz] of corners) {
    const wx = dock.posX + cosY * lx + sinY * lz;
    const wz = dock.posZ - sinY * lx + cosY * lz;
    minX = Math.min(minX, wx);
    maxX = Math.max(maxX, wx);
    minZ = Math.min(minZ, wz);
    maxZ = Math.max(maxZ, wz);
  }
  return {
    min: [minX, waterLevel - spec.pileDepth, minZ],
    max: [maxX, dock.deckTopY, maxZ],
  };
}
